import React, { useState } from "react";
import { Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { HyroTabs } from "@hyro-components";
import HyroIconButton from "@hyro-components/hyro-icon-button/HyroIconButton";
import { ICON_BUTTON_SIZES, ICON_BUTTON_VARIANTS, ICON_COLOR_MODES } from "@hyro-components/hyro-icon-button/HyroIconButton.consts";
import ChangeBadge from "./ChangeBadge";
import CountChip from "./CountChip";
import ReviewSection from "./ReviewSection";
import { CHANGE_TYPES, REVIEW_COPY } from "./reviewChanges.consts";
import { EntryHeader } from "./ReviewChangesDialog.styles";

const TabSections = ({ tab, entry }) => (
  <Stack gap={2}>
    {tab.sections.map((section, index) => (
      <ReviewSection
        key={`${section.title}-${index}`}
        section={section}
        link={tab.link}
        testId={`review-section-${entry.key}-${tab.value}-${index}`}
      />
    ))}
  </Stack>
);

// One changed resource: its header, and its changes on the same tabs as its collection page.
const ReviewEntry = ({ entry, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState(entry.tabs[0]?.value);
  const currentTab = entry.tabs.find((tab) => tab.value === activeTab) ?? entry.tabs[0];
  const hasDetails = entry.tabs.length > 0;
  const sub = [entry.sub, REVIEW_COPY.totalChanges(entry.changeCount)].filter(Boolean).join(" · ");
  const toggle = () => hasDetails && setExpanded((value) => !value);

  return (
    <Stack data-testid={`review-entry-${entry.key}`}>
      <EntryHeader expandable={hasDetails} onClick={toggle}>
        <Typography variant="body2" fontWeight={600} color="text.800">{entry.name}</Typography>
        <Typography variant="caption" color="text.700">{sub}</Typography>
        {entry.changeType !== CHANGE_TYPES.EDITED && <ChangeBadge changeType={entry.changeType} />}
        {hasDetails && (
          <HyroIconButton
            variant={ICON_BUTTON_VARIANTS.OUTLINED}
            size={ICON_BUTTON_SIZES.SMALL}
            iconColorMode={ICON_COLOR_MODES.FILL}
            sx={{ ml: "auto" }}
            aria-label={expanded ? REVIEW_COPY.COLLAPSE : REVIEW_COPY.EXPAND}
            aria-expanded={expanded}
            data-testid={`review-entry-${entry.key}-toggle`}
            onClick={(event) => {
              event.stopPropagation();
              toggle();
            }}
          >
            {expanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </HyroIconButton>
        )}
      </EntryHeader>
      {hasDetails && expanded && (
        <Stack sx={{ p: 1.5, pb: 1 }}>
          <HyroTabs
            controlled
            selectedValue={currentTab.value}
            onTabChange={setActiveTab}
            panelStyle={{ paddingTop: 16 }}
            tabsSpec={entry.tabs.map((tab) => ({
              label: tab.label,
              value: tab.value,
              endIcon: <CountChip count={tab.changeCount} active={tab.value === currentTab.value} />,
              content: <TabSections tab={tab} entry={entry} />
            }))}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default ReviewEntry;
